import { useMemo } from 'react'
import { Select } from 'antd'
import ISO6391 from 'iso-639-1'
import styled from 'styled-components'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../../styles/theme'

export interface LanguageSelectorProps {
  value: string
  onChange: (code: string) => void
}

const Wrapper = styled.div`
  margin-top: ${SPACING.xl};
  padding-top: ${SPACING.lg};
  border-top: 1px solid var(--color-border);
`

const Label = styled.p`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: var(--color-text-secondary);
  margin: 0 0 ${SPACING.sm} 0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

/** Sentinel used by the auto-detect option — sent as undefined to the backend. */
export const AUTO_LANGUAGE = ''

/** Languages surfaced at the top — covers most users without scrolling. */
const PRIORITY_CODES = ['en', 'es', 'pt', 'fr', 'de', 'it', 'no', 'sv', 'da', 'nl']

interface LanguageOption {
  value: string
  label: string
  /** Concatenated searchable text (English + native + code). */
  search: string
}

const AUTO_OPTION: LanguageOption = {
  value: AUTO_LANGUAGE,
  label: 'Auto — match venture description',
  search: 'auto automatic match venture description default',
}

function buildOptions(): LanguageOption[] {
  const allCodes: string[] = ISO6391.getAllCodes()
  const allSet = new Set(allCodes)
  const toOption = (code: string): LanguageOption => {
    const name = ISO6391.getName(code)
    const native = ISO6391.getNativeName(code)
    const label = native && native !== name ? `${name} · ${native}` : name
    return {
      value: code,
      label,
      search: `${name} ${native} ${code}`.toLowerCase(),
    }
  }
  const prioritySet = new Set(PRIORITY_CODES)
  const priority = PRIORITY_CODES.filter((c) => allSet.has(c)).map(toOption)
  const rest = allCodes
    .filter((c) => !prioritySet.has(c))
    .map(toOption)
    .sort((a, b) => a.label.localeCompare(b.label))
  return [AUTO_OPTION, ...priority, ...rest]
}

export function LanguageSelector({ value, onChange }: LanguageSelectorProps) {
  const options = useMemo(buildOptions, [])

  return (
    <Wrapper>
      <Label>Question language</Label>
      <Select
        showSearch
        value={value}
        onChange={onChange}
        options={options}
        placeholder="Auto — match venture description"
        filterOption={(input, option) =>
          (option as LanguageOption | undefined)?.search.includes(input.toLowerCase()) ?? false
        }
        style={{ width: '100%' }}
      />
    </Wrapper>
  )
}
